import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import { cleanText } from "../utils/textClean";
import "../styles/SavedJobs.css";

interface SavedJob {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  salary: string;
  description?: string;
  url: string;
  notes: string;
  savedAt: string;
}

const SavedJobs: React.FC = () => {
  const navigate = useNavigate();
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    company: "",
    minSalary: "",
    maxSalary: "",
    location: "",
    sortBy: "savedAt",
    order: "desc",
  });

  useEffect(() => {
    const fetchSavedJobs = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams();
        if (filters.company) queryParams.append("company", filters.company);
        if (filters.minSalary) queryParams.append("minSalary", filters.minSalary);
        if (filters.maxSalary) queryParams.append("maxSalary", filters.maxSalary);
        if (filters.location) queryParams.append("location", filters.location);
        queryParams.append("sortBy", filters.sortBy);
        queryParams.append("order", filters.order);

        const response = await axiosInstance.get(
          `/auth/jobs/saved?${queryParams.toString()}`
        );
        setSavedJobs(response.data.jobs);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error || "Failed to load saved jobs");
        setSavedJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSavedJobs();
  }, [filters]);

  const handleRemoveJob = async (jobId: string) => {
    if (!window.confirm("Remove this job from saved?")) return;

    try {
      await axiosInstance.delete(`/auth/jobs/saved/${jobId}`);
      setSavedJobs(savedJobs.filter((job) => job.id !== jobId));
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove job");
    }
  };

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="saved-jobs-container">
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          marginBottom: "20px",
          padding: "10px 20px",
          background: "#667eea",
          color: "white",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#5568d3";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#667eea";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        ← Back to Dashboard
      </button>

      <h1>📌 Saved Jobs</h1>

      <div className="filters-section">
        <h3>Filter & Sort</h3>
        <div className="filters-grid">
          <input
            type="text"
            name="company"
            placeholder="Company name"
            value={filters.company}
            onChange={handleFilterChange}
          />
          <input
            type="number"
            name="minSalary"
            placeholder="Min salary"
            value={filters.minSalary}
            onChange={handleFilterChange}
          />
          <input
            type="number"
            name="maxSalary"
            placeholder="Max salary"
            value={filters.maxSalary}
            onChange={handleFilterChange}
          />
          <input
            type="text"
            name="location"
            placeholder="Location"
            value={filters.location}
            onChange={handleFilterChange}
          />
          <select
            name="sortBy"
            value={filters.sortBy}
            onChange={handleFilterChange}
          >
            <option value="savedAt">Date Saved</option>
            <option value="jobTitle">Job Title</option>
            <option value="company">Company</option>
          </select>
          <select
            name="order"
            value={filters.order}
            onChange={handleFilterChange}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      {loading && <p className="loading">Loading saved jobs...</p>}
      {error && <p className="error">❌ {error}</p>}

      {!loading && savedJobs.length === 0 && (
        <p className="no-jobs">No saved jobs yet. Start saving interesting jobs!</p>
      )}

      <div className="jobs-list">
        {savedJobs.map((job) => (
          <div key={job.id} className="job-card">
            <div className="job-header">
              <div className="job-info">
                <h3>{job.jobTitle}</h3>
                <p className="company">{job.company}</p>
              </div>
              <button
                className="remove-btn"
                onClick={() => handleRemoveJob(job.id)}
                title="Remove from saved"
              >
                ✕
              </button>
            </div>

            <div className="job-details">
              <span className="location">📍 {job.location}</span>
              <span className="salary">💰 {job.salary}</span>
            </div>

            {job.description && (
              <p className="job-description">
                {/* cleanText: rows synced before the HTML-normalization fix may store raw HTML */}
                {cleanText(job.description).substring(0, 220)}{cleanText(job.description).length > 220 ? "…" : ""}
              </p>
            )}

            {job.notes && (
              <div className="notes">
                <strong>Notes:</strong> {job.notes}
              </div>
            )}

            <p className="saved-date">
              Saved: {new Date(job.savedAt).toLocaleDateString()}
            </p>

            <a href={job.url} target="_blank" rel="noopener noreferrer" className="apply-btn">
              View Job →
            </a>
          </div>
        ))}

      </div>
    </div>
  );
};

export default SavedJobs;
